import {DestroyRef, inject, Injectable} from '@angular/core';
import {BehaviorSubject, combineLatestWith, defer, EMPTY, iif, merge, Observable, of, switchMap} from 'rxjs';
import {map, take} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';

import { MenuStoreService } from './menu-store.service';
import { MenuApiService } from '@emm/menu/services/menu-api.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Consumer} from '@emm/core/models/consumer.model';
import {Venue} from '@emm/core/models/venue.model';

@Injectable({
    providedIn: 'root'
})
export class MenuFiltersFacadeService {

    changeSets$ = this.menuStore.queryChangeSets.all$;
    consumers$ = new BehaviorSubject<Consumer[]>(null);
    consumerTypes$ = this.menuStore.queryConsumerTypes.all$;
    effectiveDate$ = this.menuStore.queryBase.effectiveDate$;
    isChainMenu$ = this.menuStore.queryLocationSets.isChainMenu$;
    isLocationSetTypeParent$ = this.menuStore.queryLocationSetTypes.isLocationSetTypeParent$;
    locationSets$ = this.menuStore.queryLocationSets.filteredLocationSets$;
    locationSetTypes$ = this.menuStore.queryLocationSetTypes.onlyParents$;
    parentLabel$ = null;
    venues$ = new BehaviorSubject<Venue[]>(null);
    selectedVenue: Venue;
    selectedVenue$ = this.menuStore.queryLocationSets.selectedVenue$;
    selectedSubmenu$ = this.menuStore.querySubmenus.selected$;

    // If the location set is a parent return the location set else return empty observable
    isParentLocationSets$ = this.isLocationSetTypeParent$
        .pipe(
            switchMap((isParent) => isParent ? this.locationSets$ : EMPTY)
        );

    // When the filter for location sets has changed
    // filter the parent location set by that value but only if filter is valid
    // else return parent location sets
    parentLocationFilterSet$ = this.menuStore.queryLocationSets.filter$
        .pipe(
            switchMap((filterStr) =>
                iif(() => filterStr !== null && filterStr !== '',
                    defer(() => this.isParentLocationSets$
                        .pipe(
                            map((sets) =>
                                sets.filter(item => item.name?.toLowerCase().includes(filterStr?.toLowerCase())))
                )),
                    defer(() => this.isParentLocationSets$)
                )
            )
        );

    // Merge the observables so in either case the parent location set has a value
    parentLocationSets$ = merge(this.isParentLocationSets$, this.parentLocationFilterSet$);

    childLocationSets$ = this.menuStore.queryLocationSets.filteredLocationSets$;

    selectedFeatureScope$ = this.menuStore.queryFeatureScope.selected$;
    selectedLocationSetType$ = this.menuStore.queryLocationSetTypes.selected$;
    selectedLocationSet$ = this.menuStore.queryLocationSets.selected$;
    selectedConsumer$ = this.menuStore.queryConsumers.selected$;

    isLoading$ = of([]).pipe(combineLatestWith([
            this.menuStore.isLoading$.attributes,
            this.menuStore.isLoading$.changeSets,
            this.menuStore.isLoading$.consumerTypes,
            this.menuStore.isLoading$.consumers,
            this.menuStore.isLoading$.locationSetTypes,
            this.menuStore.isLoading$.locationSets,
        ]),
        map((sources: any[]) => R.any(R.equals(true), sources))
    );

    private readonly destroyRef: DestroyRef = inject(DestroyRef);


    constructor(private menuStore: MenuStoreService,
                private api: MenuApiService) {
        this.menuStore.queryConsumers.all$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => this.consumers$.next(result));
    }



    selectFeatureScope(scope: string): void {
        this.menuStore.dispatch.selectFeatureScope(scope);
    }



    selectLocationSetType(tag: string): void {
        this.menuStore.dispatch.selectLocationSetType(tag);
        this.menuStore.dispatch.loadLocationsByParent(tag);
    }



    selectLocationSet(tag: string): void {
        this.menuStore.dispatch.selectLocationSet(tag);
        let featureScope;
        this.selectedFeatureScope$.pipe(take(1)).subscribe(scope => featureScope = scope);
        if (featureScope !== 'Global') {
            this.api.getVenuesByLocation(tag)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(venues => {
                    const temp: Venue[] = [];
                    temp.push({
                        name: 'Base Location',
                        tag,
                        id: tag,
                        locationId: tag,
                        venueId: tag,
                        venueNumber: tag,
                        venueType: null,
                        deleted: false,
                        archived: false,
                        dateCreated: null,
                        createdBy: 'system',
                        dateUpdated: null,
                        updatedBy: 'system'
                    });
                                       for (let i = 0; i < venues.length; i++){
                        temp.push(venues[i]);
                    }
                    this.venues$.next(temp);
                });
        }
    }



    selectVenue(venue: Venue): void {
        this.menuStore.dispatch.selectVenue(venue);
        this.selectedVenue = venue;
        if (venue?.name === 'Base Location') {
            this.api.getAllConsumers()
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(result => this.consumers$.next(result));
        }
        else if (venue) {
            this.api.getConsumersByVenue(venue?.venueId)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(
                result => this.consumers$.next(result)
            );
        }
    }



    changeEffectiveDate(effectiveDate: string): void {
        this.menuStore.dispatch.changeEffectiveDate(effectiveDate);
    }



    selectConsumer(tag: string): void {
        this.menuStore.dispatch.selectConsumer(tag);
    }



    deleteConsumer(tag: string): void {
        this.menuStore.dispatch.deleteConsumer(tag);
    }



    applyLocationSetFilter(locationSetFilter: string): void {
        this.menuStore.dispatch.updateFilter(locationSetFilter);
    }



    selectParentLocationSet(tag: string): void {
        this.menuStore.dispatch.selectLocationSet(tag);
        this.menuStore.dispatch.loadLocationsByParent(tag);
    }



    loadConsumerTypes(): void {
        this.menuStore.dispatch.loadConsumerTypes();
    }



    reloadConsumers(): void {
        this.menuStore.dispatch.reloadConsumers();
    }



}
